import { ReactNode } from 'react';
import { Environment } from 'react-relay';

import authModalLoader from '../../utils/AuthModalLoader';
import { fetchUserMetadata } from '../helpers/fetchUserMetadata';
import { authTrackingHandler } from './authTrackingHandler';
import { trackRegistrationError } from './authTracking';
import { registrationMutation } from './registration/registrationMutation';
import { validateEmail, validateEmailString } from './authValidation';
import { completeAccountEmailMutation } from './registration/completeAccountEmailMutation';
import { DEFAULT_FLOW, LOGIN, AUTH_MODAL, AUTH_CONTENT } from './authFlowConstants';
import authCopy from './authCopy';

import { AuthResponseType } from './authTypes';

type RegistrationDataType = {
    email: string;
    errorHandler?: (validationError: ReactNode) => void;
    loadingHandler?: (param: boolean) => void;
    userRegistrationHandler?: (userRegistration: AuthResponseType) => void;
    userLoginHandler?: () => void;
    showAuthHandler?: (authHandlerParams: {
        showExistsError: boolean;
        flow: string;
        action: string;
        ga: { label: string };
        email: string;
        onAuthComplete?: () => void;
    }) => Promise<void>;
    authType?: string;
    environment: Environment;
    gaLabel: string;
    flow?: string;
    marketingOptIn?: boolean;
    isGdprApplicable?: boolean;
};

/**
 * helper function which handles email harvesting flows
 * accepts an email from an input (i.e. follow search banner, footer subscribe)
 * checks status of that email:
 *  a) if email is attached to existing user, prompt them to login or create PW for their account
 *  b) if email is not registered, create email only account for that user
 *
 * @param {String} email email being checked
 * @param {Function} errorHandler function for error handling
 * @param {Function} loadingHandler function for loading state handling
 * @param {Function} userRegistrationHandler callback after email only account is created for user, passed user data
 * @param {Function} userLoginHandler callback after user logs in, passed user data
 * @param {Object} environment relay environment
 * @param {String} gaLabel label value for auth
 * @param {String} flow auth flow constant
 * @param {Boolean} marketingOptIn has the user opted in to marketing campaign
 * @param {Boolean} isGdprApplicable is the user in EU or GDPR-covered region
 */
export const registrationOrLoginHandler = ({
    email,
    errorHandler = () => {},
    loadingHandler = () => {},
    userRegistrationHandler = () => {},
    userLoginHandler = () => {},
    showAuthHandler = authModalLoader.show.bind(authModalLoader),
    authType = AUTH_MODAL,
    environment,
    gaLabel,
    flow = DEFAULT_FLOW,
    marketingOptIn = true,
    isGdprApplicable = true,
}: RegistrationDataType): void => {
    const validationError = validateEmail(email);
    if (validationError) {
        const errorString = validateEmailString(email);
        trackRegistrationError({ field: 'email', error: errorString });
        errorHandler(validationError);
        return;
    }
    loadingHandler(true);
    fetchUserMetadata(email, environment).then(
        ({ disableCompleteAccountEmail, userExistsMetadata }) => {
            if (!userExistsMetadata?.userExists) {
                const ga = { label: gaLabel };

                registrationMutation(environment, {
                    email,
                    marketingOptIn: !isGdprApplicable || marketingOptIn, // automatically opt-in non-GDPR to marketing
                    ga,
                })
                    // @ts-ignore registrationMutation -> TS
                    .then(({ userRegistration }: { userRegistration: AuthResponseType }) => {
                        authTrackingHandler({ userRegistration, environment, ga });
                        if (!disableCompleteAccountEmail) {
                            completeAccountEmailMutation({ email, environment });
                        }
                        userRegistrationHandler(userRegistration);
                    })
                    .catch(() => {
                        loadingHandler(false);
                        errorHandler(authCopy.errors.support);
                    });
            } else {
                trackRegistrationError({
                    field: 'email',
                    action: gaLabel,
                    error: 'existing email',
                });
                const authHandlerParams = {
                    showExistsError: true,
                    flow,
                    action: LOGIN,
                    ga: { label: gaLabel },
                    email,
                };
                switch (authType) {
                    case AUTH_MODAL:
                        showAuthHandler(authHandlerParams).then(userLoginHandler);
                        break;
                    case AUTH_CONTENT:
                        showAuthHandler({
                            ...authHandlerParams,
                            onAuthComplete: userLoginHandler,
                        });
                        break;
                }
            }
        }
    );
};
